import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "424",
  "slug": "hottest-places-on-earth",
  "title": "The Hottest Places on Earth",
  "excerpt": "From the air-temperature record at Death Valley to the scorched ground of Iran’s Lut Desert, here are the hottest spots on the planet and why they bake.",
  "date": "2026-06-13",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1755686972125-36a4b3330291?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxkZXNlcnQlMjBoZWF0JTIwZHVuZXN8ZW58MXwwfHx8MTc4MTMzODUxNXww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Death Valley holds the highest reliably recorded air temperature at 56.7C, but the Lut Desert in Iran has registered the hottest land surface temperatures from satellites at over 80C. Dallol in Ethiopia is the hottest inhabited place by annual average.",
  "content": [
    {
      "type": "paragraph",
      "text": "Asking for the single hottest place on Earth sounds simple, but it depends entirely on what you measure. Air temperature in the shade, the temperature of the ground itself, and the average across a whole year each crown a different champion. Get clear on which one you mean, and the contenders sort themselves out, along with the geography that turns them into ovens."
    },
    {
      "type": "heading",
      "text": "Three Ways to Measure Heat"
    },
    {
      "type": "paragraph",
      "text": "Before the rankings, it helps to separate the three measurements that get tangled together. Air temperature is what a standard weather station records in the shade, about a metre and a half above the ground. Surface temperature is the heat of the land itself, which can be dramatically hotter than the air above it. Annual mean temperature averages every day of the year, rewarding places that are relentlessly warm rather than briefly extreme. Each one names a different winner."
    },
    {
      "type": "heading",
      "text": "The Air Temperature Record: Death Valley"
    },
    {
      "type": "paragraph",
      "text": "The highest air temperature ever reliably recorded on Earth was 56.7 degrees Celsius, or 134 degrees Fahrenheit, measured at Furnace Creek in Death Valley, California, on 10 July 1913. The reading is occasionally disputed by climate historians, but the World Meteorological Organization still recognises it as the official record."
    },
    {
      "type": "paragraph",
      "text": "Death Valley keeps producing extreme heat to this day, with summer readings around 54 degrees Celsius in recent years. Its recipe is geography in miniature: the valley floor sits below sea level, hot air sinks and compresses as it descends, steep surrounding mountains trap that air, and the dry, dark ground radiates heat back upward. Nothing about the place lets the heat escape, so it accumulates like an oven left on all day."
    },
    {
      "type": "heading",
      "text": "The Hottest Ground: The Lut Desert"
    },
    {
      "type": "paragraph",
      "text": "If you measure the temperature of the land surface rather than the air above it, the record moves to Iran. Satellites carrying thermal sensors have clocked the Dasht-e Lut, or Lut Desert, at surface temperatures above 80 degrees Celsius, the hottest ground ever detected from orbit. Almost no weather stations exist out there, so this record was found by satellite rather than a thermometer on the ground, a reminder of how much extreme geography we only know about because of Earth observation."
    },
    {
      "type": "paragraph",
      "text": "Surface temperature and air temperature are not the same thing, and the gap between them can be enormous. Dark, dry sand and rock can sizzle far hotter than the air just above them. That is why the Lut can hold the surface crown while Death Valley holds the air-temperature one; they are answers to two different questions."
    },
    {
      "type": "heading",
      "text": "The Hottest Place to Actually Live: Dallol"
    },
    {
      "type": "paragraph",
      "text": "For the hottest inhabited spot, head to the Danakil Depression in Ethiopia. The settlement of Dallol recorded one of the highest average annual temperatures anywhere people have lived, hovering around 34 to 35 degrees Celsius averaged across every single day of the year. There is no cool season to look forward to. It is brutally hot in what passes for winter just as it is in summer."
    },
    {
      "type": "paragraph",
      "text": "Dallol sits in a volcanic, below-sea-level basin dotted with neon-coloured acid springs and salt flats, one of the most alien-looking landscapes on the planet. It is a vivid reminder that extreme heat and extreme geology tend to show up in the same places."
    },
    {
      "type": "heading",
      "text": "Other Furnaces Worth Knowing"
    },
    {
      "type": "list",
      "items": [
        "Mitribah, Kuwait reached 53.9 degrees Celsius in 2016, among the highest reliably recorded temperatures in Asia.",
        "Kebili, Tunisia is often cited with readings around 55 degrees Celsius, though older African records are hard to verify.",
        "The Flaming Mountains of Xinjiang, China earn their name with scorching summer surface temperatures across bare red sandstone.",
        "Australia’s outback regularly pushes past 50 degrees Celsius during severe heatwaves."
      ]
    },
    {
      "type": "heading",
      "text": "What Makes a Place This Hot"
    },
    {
      "type": "paragraph",
      "text": "The hottest places on Earth almost always share a few ingredients. They sit in subtropical latitudes near 30 degrees north or south, where descending dry air suppresses rain and clouds. They are far from the cooling influence of oceans. The ground is dark and barren, with no vegetation or moisture to soak up the energy. And many sit in low basins where hot air pools and compresses instead of draining away."
    },
    {
      "type": "paragraph",
      "text": "Notice what is missing from that list: the equator. The wettest, cloudiest tropics rarely set heat records, because all that humidity and cloud cover caps the daytime peak. High humidity makes the air feel more dangerous to humans, but it actually limits the absolute temperature. The true furnaces are the dry subtropical deserts a little further from the middle of the map."
    }
  ]
};
